import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { IpdTaskView, IpdTaskWindow, IpdWardTaskWindow, StaffDuty } from '@his/shared';
import { get } from '@/lib/api';
import { useMinuteClock } from '../emr/emar-clock';
import { clockOffset } from './task-chart';

export const STAFF_DUTY_KEY = ['staff-duty'] as const;
export const WITNESSES_KEY = ['emar-witnesses'] as const;
export const TASK_READ_PERMISSIONS = ['emar.read', 'emar.administer', 'nursing.note.write'] as const;
export const TASK_RECORD_PERMISSIONS = ['emar.administer', 'nursing.note.write', 'vitals.write'] as const;

export interface WitnessCandidate {
  id: string;
  name: string;
  designation: string | null;
}

export function wardTasksKey(wardId: string) {
  return ['ipd-ward-tasks', wardId] as const;
}

export function admissionTasksKey(admissionId: string, from?: string) {
  return from === undefined ? (['ipd-tasks', admissionId] as const) : (['ipd-tasks', admissionId, from] as const);
}

export function fetchWardTasks(wardId: string): Promise<IpdWardTaskWindow> {
  return get<IpdWardTaskWindow>(`/ipd/wards/${encodeURIComponent(wardId)}/tasks`);
}

export function fetchStaffDuty(): Promise<StaffDuty> {
  return get<StaffDuty>('/staff-alerts/me/duty');
}

export function useStaffDuty(enabled = true) {
  return useQuery({ queryKey: STAFF_DUTY_KEY, queryFn: fetchStaffDuty, staleTime: 5 * 60_000, enabled });
}

export function useWitnesses(enabled: boolean) {
  return useQuery({
    queryKey: WITNESSES_KEY,
    queryFn: () => get<WitnessCandidate[]>('/ipd/emar/witnesses'),
    enabled,
    staleTime: 300_000,
  });
}

export function mayRecordTask(can: (...permissions: string[]) => boolean, task: Pick<IpdTaskView, 'taskKind'>): boolean {
  return task.taskKind === 'dose' ? can('emar.administer') : can(...TASK_RECORD_PERMISSIONS);
}

export function replaceTask<T extends { data: IpdTaskView[] }>(window: T | undefined, task: IpdTaskView): T | undefined {
  if (!window) return window;
  return { ...window, data: window.data.map((row) => (row.id === task.id ? { ...row, ...task } : row)) };
}

export function storeRecordedTask(queryClient: QueryClient, task: IpdTaskView, wardId?: string | null): void {
  queryClient.setQueriesData<IpdTaskWindow>({ queryKey: admissionTasksKey(task.admissionId) }, (window) => replaceTask(window, task));
  if (wardId) queryClient.setQueryData<IpdWardTaskWindow>(wardTasksKey(wardId), (window) => replaceTask(window, task));
}

export function useTaskClock(serverTime: string | undefined, receivedAt: number): number {
  const clock = useMinuteClock();
  return clock + clockOffset(serverTime, receivedAt);
}
